import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Separator } from '@change/ds-core-ui/Separator';
import { Spinner } from '@change/ds-core-ui/Spinner';
import { Text } from '@change/ds-core-ui/Text';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import {
	useDecisionMakerProfilePageData,
	useDMProfilePagePaginatedResponsesData,
} from '#src/app/pages/decisionMaker/profile/pageContext';
import { useLazyLoadedListFocus } from '#src/app/shared/hooks/focus';
import { useQueryParamValue } from '#src/app/shared/hooks/location';

import { ResponseListCard } from './components/ResponseListCard';
import { useResponseList } from './hooks/useResponseList';

export const ResponseList: React.FC<EmptyProps> = () => {
	const {
		data: { displayName, responses, hasNextPage, nextPageLoading, showDmrlSeparator },
		actions: { loadMore },
	} = useResponseList();

	const {
		data: { totalCount: responsesCount, totalCountLocalized: responsesCountLocalized },
	} = useDMProfilePagePaginatedResponsesData();

	const {
		data: {
			featureConfigs: { dmReputationLayer },
		},
	} = useDecisionMakerProfilePageData();

	const showDmrl = useQueryParamValue('showDmrl');
	const isDmrlEnabled = dmReputationLayer.enabled || showDmrl === 'true';

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate } = useI18n();

	if (!responses.length) return null;

	return (
		<div data-qa="dm-responses-content" ref={containerRef} className="mb-10 md:mb-14">
			{showDmrlSeparator && <Separator orientation="horizontal" className="mb-14" />}
			<Text as="h2" variant="heading" size="small" className="mb-5 md:mb-10">
				{isDmrlEnabled ? (
					<TranslatePlural
						value="corgi.decision_maker_profile.responses_list.header_with_count"
						count={responsesCount}
						replacements={{ displayName, count: responsesCountLocalized }}
					/>
				) : (
					<Translate value="corgi.decision_maker_profile.responses_list.header" replacements={{ displayName }} />
				)}
			</Text>

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{responses.map((response, index) =>
					response.petition ? (
						<div key={response.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.decision_maker_profile.responses_list.fetched_more"
										count={responses.length - lastEvent.previousItemsCount}
										replacements={{ count: responses.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<ResponseListCard
								responseId={response.id}
								message={response.petition.redacted ? '' : response.message}
								redacted={response.petition.redacted}
								petitionHref={`/p/${encodeURIComponent(response.petition.slug)}`}
								petitionAsk={
									response.petition.redacted
										? translate('corgi.components.petition_card.under_review')
										: response.petition.ask
								}
								petitionId={response.petition.id}
								ref={getListItemRef(index)}
							/>
						</div>
					) : null,
				)}
			</div>

			{nextPageLoading && (
				<div className="mt-16">
					<div className="sr-only" role="alert">
						<Translate value="corgi.decision_maker_profile.responses_list.fetching_more" />
					</div>
					<Spinner className="mx-auto" ref={loadingRef} data-testid="load-more-btn" />
				</div>
			)}

			{hasNextPage && !nextPageLoading && (
				<Button
					className="mt-5 mb-6 basis-full md:mt-10 md:basis-1/3"
					size="small"
					onClick={() => {
						recordListEvent({ type: 'loadMore', itemsCount: responses.length });
						loadMore();
					}}
					data-qa="dm-responses-load-more"
					ref={loadMoreRef}
				>
					{translate('corgi.decision_maker_profile.load_more')}
				</Button>
			)}
		</div>
	);
};
